import { motion } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";
import { StatusBadge, SeverityTag } from "@/components/ui/Badge";

// The floating "product shot" beside the hero — a fake live test run that
// animates its steps checking off. Sells the core experience at a glance.
const STEPS = [
  { label: "Navigate to /login", done: true },
  { label: "Fill email field", done: true },
  { label: "Click “Log in”", done: true },
  { label: "Assert error message visible", done: false },
];

export default function RunPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotateX: 8 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="card relative w-full max-w-md overflow-hidden p-5"
    >
      {/* header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-500/15 text-brand-500">
            <Sparkles size={15} />
          </span>
          Test run
        </div>
        <StatusBadge status="FAILED" />
      </div>

      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        &ldquo;Log in with a wrong password and check the error shows.&rdquo;
      </p>

      {/* live plan */}
      <div className="mt-4 space-y-2">
        {STEPS.map((step, i) => (
          <motion.div
            key={step.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + i * 0.25 }}
            className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5 text-sm dark:border-white/5 dark:bg-white/5"
          >
            {step.done ? (
              <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                <Check size={13} />
              </span>
            ) : (
              <span className="grid h-5 w-5 place-items-center rounded-full bg-rose-500 text-white">
                <span className="text-[0.6875rem] font-bold">!</span>
              </span>
            )}
            <span
              className={
                step.done
                  ? "text-slate-700 dark:text-slate-200"
                  : "font-medium text-rose-600 dark:text-rose-300"
              }
            >
              {step.label}
            </span>
          </motion.div>
        ))}
      </div>

      {/* validating shimmer line */}
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
        <Loader2 size={13} className="animate-spin" />
        Validating outcome with judge model&hellip;
      </div>

      {/* bug result */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="mt-4 rounded-xl border border-rose-200 bg-rose-50/60 p-3.5 dark:border-rose-500/20 dark:bg-rose-500/10"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-800 dark:text-white">
            🐞 No error shown on bad login
          </span>
          <SeverityTag severity="high" />
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span>⏱ 18s</span>
          <span>·</span>
          <span>$0.004 · 1,240 tok</span>
        </div>
      </motion.div>
    </motion.div>
  );
}
